import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { collection, doc, getDoc, onSnapshot } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Loader2, LogOut, X } from 'lucide-react';
import { useAuth } from '../utils/AuthContext';
import { db } from '../utils/firebase';
import { getPublicSettings, type AppSettings } from '../utils/appSettings';
import { isSubcontractorRole, canPreviewPartnerPortals } from '../utils/userRoles';
import type { TradeBriefingLink } from '../types';
import {
  buildTradeCompanyOptions,
  tradeCompanyIdentityKey,
} from '../utils/tradeBriefingCompanies';
import {
  parseTradePortalPreview,
  previewFromCompany,
  tradeLinkMatchesPreview,
  tradePortalPreviewHref,
} from '../utils/tradePortalPreview';
import TradeBriefingChrome, {
  useResolvedPortalBrandingLogo,
} from '../components/tradeBriefing/TradeBriefingChrome';
import Login from './Login';
import './TradeBriefingPage.css';

const TRADE_LABELS: Record<string, string> = {
  finishing: 'Finishing',
  millwork: 'Millwork',
  electrical: 'Electrical',
  roofing: 'Roofing',
  general: 'General',
  other: 'Other',
};

type InboxRow = {
  id: string;
  token: string;
  companyName: string;
  tradeType: string;
  displayName?: string;
  projectName?: string;
  inviteStatus?: string;
};

type LinkRow = TradeBriefingLink & { id: string };

const TradeBriefingInboxPage: React.FC = () => {
  const { user, isAuthenticated, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [settings, setSettings] = useState<AppSettings | null>(null);
  const [callableRows, setCallableRows] = useState<InboxRow[]>([]);
  const [allLinks, setAllLinks] = useState<LinkRow[]>([]);
  const [projectNames, setProjectNames] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const canPreview = canPreviewPartnerPortals(user?.role);
  const isSubcontractor = isSubcontractorRole(user?.role);
  const preview = canPreview ? parseTradePortalPreview(searchParams) : null;
  const companies = useMemo(() => buildTradeCompanyOptions(allLinks), [allLinks]);

  useEffect(() => {
    getPublicSettings().then(setSettings).catch(() => setSettings(null));
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (
      isAuthenticated
      && user
      && !isSubcontractorRole(user.role)
      && !canPreviewPartnerPortals(user.role)
    ) {
      navigate('/', { replace: true });
    }
  }, [authLoading, isAuthenticated, user, navigate]);

  useEffect(() => {
    if (!canPreview || !isAuthenticated) {
      setAllLinks([]);
      return;
    }
    const unsub = onSnapshot(
      collection(db, 'tradeBriefingLinks'),
      snap => {
        setAllLinks(snap.docs.map(d => ({ id: d.id, ...(d.data() as TradeBriefingLink) })));
      },
      err => {
        console.error('[TradeBriefingInbox]', err);
        setError('Could not load trades.');
      },
    );
    return () => unsub();
  }, [canPreview, isAuthenticated]);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated || !user?.id) {
      setLoading(false);
      return;
    }
    if (canPreview) {
      setLoading(false);
      return;
    }
    if (!isSubcontractor) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const fn = httpsCallable<Record<string, never>, { briefings: InboxRow[] }>(
          getFunctions(undefined, 'us-central1'),
          'listMyTradeBriefings',
        );
        const { data } = await fn({});
        if (!cancelled) setCallableRows(data.briefings || []);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Could not load briefings.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authLoading, isAuthenticated, user?.id, canPreview, isSubcontractor]);

  const staffRows = useMemo(() => {
    if (!canPreview || !preview) return [];
    return allLinks
      .filter(link => !link.revokedAt && tradeLinkMatchesPreview(link, preview))
      .map(link => ({
        id: link.id,
        token: link.id,
        companyName: link.companyName || '',
        tradeType: link.tradeType || 'general',
        displayName: link.displayName || '',
        projectName: projectNames[link.timberProjectId] || '',
        inviteStatus: link.inviteStatus || (link.invitedUid ? 'accepted' : 'pending'),
        timberProjectId: link.timberProjectId,
      }));
  }, [allLinks, canPreview, preview, projectNames]);

  useEffect(() => {
    if (!canPreview || !preview) return;
    const ids = Array.from(
      new Set(
        allLinks
          .filter(link => !link.revokedAt && tradeLinkMatchesPreview(link, preview))
          .map(link => link.timberProjectId)
          .filter(Boolean),
      ),
    );
    if (!ids.length) {
      setProjectNames({});
      return;
    }
    let cancelled = false;
    void Promise.all(ids.map(id => getDoc(doc(db, 'timberProjects', id)))).then(snaps => {
      if (cancelled) return;
      const next: Record<string, string> = {};
      snaps.forEach(snap => {
        if (snap.exists()) next[snap.id] = String(snap.data()?.name || '').trim();
      });
      setProjectNames(next);
    });
    return () => {
      cancelled = true;
    };
  }, [allLinks, canPreview, preview]);

  const rows = canPreview ? staffRows : callableRows;
  const previewCompany = preview
    ? companies.find(company => {
        if (preview.uid && company.invitedUid === preview.uid) return true;
        if (preview.email && company.inviteEmail === preview.email) return true;
        if (preview.companyKey && company.key === preview.companyKey) return true;
        return false;
      }) || companies.find(company =>
        allLinks.some(
          link =>
            tradeCompanyIdentityKey(link) === company.key && tradeLinkMatchesPreview(link, preview),
        ),
      )
    : null;

  const { src: brandingLogoSrc, onImgError: onBrandingLogoError } = useResolvedPortalBrandingLogo(
    settings?.engagementLogo ?? null,
  );
  const backgroundUrl =
    settings?.portalBackgroundImage?.url || settings?.engagementBackground?.url || null;

  const switchPreview = (companyKey: string) => {
    const company = companies.find(row => row.key === companyKey);
    if (!company) {
      navigate('/briefings', { replace: true });
      return;
    }
    navigate(tradePortalPreviewHref(previewFromCompany(company)), { replace: true });
  };

  if (authLoading) {
    return (
      <TradeBriefingChrome
        backgroundUrl={backgroundUrl}
        logoSrc={brandingLogoSrc}
        onLogoError={onBrandingLogoError}
      >
        <div className="tb-gate">
          <Loader2 size={28} className="tb-spin" />
          <p>Loading…</p>
        </div>
      </TradeBriefingChrome>
    );
  }

  if (!isAuthenticated) {
    return <Login />;
  }

  return (
    <TradeBriefingChrome
      backgroundUrl={backgroundUrl}
      logoSrc={brandingLogoSrc}
      onLogoError={onBrandingLogoError}
    >
      <article className="tb-sheet">
        {canPreview ? (
          <div className="tb-preview-bar">
            <label>
              Preview as
              <select
                aria-label="Preview as trade"
                value={previewCompany?.key || ''}
                onChange={e => switchPreview(e.target.value)}
              >
                {!previewCompany ? <option value="">Choose a trade…</option> : null}
                {companies.map(company => (
                  <option key={company.key} value={company.key}>
                    {company.companyName}
                    {company.projectCount > 1 ? ` · ${company.projectCount} projects` : ''}
                  </option>
                ))}
              </select>
            </label>
            {preview ? (
              <button type="button" className="tb-text-btn" onClick={() => navigate('/briefings')}>
                <X size={14} />
                Exit preview
              </button>
            ) : null}
          </div>
        ) : (
          <div className="tb-inbox-user">
            <span>{user?.name || user?.email}</span>
            <button type="button" className="tb-text-btn" onClick={() => void logout()}>
              <LogOut size={14} />
              Sign out
            </button>
          </div>
        )}

        <header className="tb-masthead">
          <p className="tb-kicker">Trade portal</p>
          <h1>
            {canPreview
              ? previewCompany?.companyName || 'Preview a trade'
              : rows[0]?.companyName || user?.name || 'Projects'}
          </h1>
          <p className="tb-meta-line">
            {canPreview
              ? preview
                ? 'Projects assigned to this trade'
                : 'Choose a company to see every project they have with Leiper\'s Creek Timber Frames.'
              : 'Your Leiper\'s Creek Timber Frames projects'}
          </p>
        </header>

        {loading ? (
          <div className="tb-gate">
            <Loader2 size={24} className="tb-spin" />
            <p>Loading projects…</p>
          </div>
        ) : error ? (
          <p className="tb-quote-error">{error}</p>
        ) : !preview && canPreview ? (
          <p className="tb-inbox-empty">Select a trade above to preview their portal.</p>
        ) : rows.length === 0 ? (
          <p className="tb-inbox-empty">No briefings have been assigned yet.</p>
        ) : (
          <div className="tb-table-wrap">
            <table className="tb-table">
              <thead>
                <tr>
                  <th scope="col">Project</th>
                  <th scope="col">Trade</th>
                  <th scope="col">Open</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(row => (
                  <tr key={row.id}>
                    <td className="tb-file-name">{row.projectName || row.displayName || row.companyName || 'Trade briefing'}</td>
                    <td>{[TRADE_LABELS[row.tradeType] || row.tradeType, row.displayName].filter(Boolean).join(' · ')}</td>
                    <td className="tb-table-actions">
                      <Link to={`/briefing/${row.token}`}>Open</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <footer className="tb-footer">
          <p>Leiper&apos;s Creek Timber Frames</p>
        </footer>
      </article>
    </TradeBriefingChrome>
  );
};

export default TradeBriefingInboxPage;
